import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
	toImmutableMap,
} from '@datachain/ui-sdk/common';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { randUuid } from '@ngneat/falso';
import { Map, OrderedMap } from 'immutable';

import {
	CharacteristicType,
	ConnectorSubType,
	DcTimestampFormat,
	DepotTypes,
} from '../core/dc-core-types';
import { DxListsStorage } from '../core/dx-lists.storage';
import { DcDxGrids } from '../core/dx-types';
import { ProjectStorage } from '../core/project.storage';
import { TagAdapter } from '../core/tag.adapter';
import { DcRouteUrlBuilder } from '../core/utils/dc-route-url-builder';
import {
	ICharacteristicType,
	SupportedTypes,
} from '../requesters/core/models/characteristic-type.model';
import { IDateFormat } from '../requesters/core/models/date-format.model';
import { ISupportedLanguage } from '../requesters/core/models/language.model';
import {
	ColumnTypeModel,
	IDepotEntityBulkCount,
	IDepotHeaderResponse,
	IDepotResponse,
	IDepotSchemaModel,
	ISchemaDefinitionModel,
} from '../requesters/depot/depot.model';
import { DepotHeaderItemEntity } from './components/create-entity-from-depot-modal/depot-header-item.entity';
import { DepotHeaderListEntity } from './components/create-entity-from-depot-modal/depot-header-list.entity';
import { DepotItemEntity } from './components/depot-list/depot-item.entity';
import {
	DepotListEntity,
	IDepotListExtra,
} from './components/depot-list/depot-list.entity';
import {
	DepotSchemaEntity,
	IDepotSchema,
	SchemaDataStructure,
} from './components/depot-schema-viewer/depot-schema.entity';
import { SupportedLanguageEntity } from './domain/supported-language.entity';

@Injectable()
export class DepotAdapter {
	private readonly ELEMENT_LABEL = '';
	private readonly ROOT_PARENT_LABEL = '';
	private readonly dataTypeMapper: { [k: string]: ColumnType } = {
		['string']: ColumnType.Str,
		file: ColumnType.File,
		double: ColumnType.Double,
		decimal: ColumnType.Dec,
		date: ColumnType.Date,
		biginteger: ColumnType.BigInt,
		binary: ColumnType.Bin,
		words: ColumnType.Words,
		integer: ColumnType.Int,
		['boolean']: ColumnType.Bool,
		geometry: ColumnType.Geo,
	};

	private readonly typeMapper: { [k: string]: string } = {
		[ColumnType.Str]: 'string'.toUpperCase(),
		[ColumnType.Unknown]: 'string'.toUpperCase(),
		[ColumnType.File]: 'file'.toUpperCase(),
		[ColumnType.Double]: 'double'.toUpperCase(),
		[ColumnType.Dec]: 'decimal'.toUpperCase(),
		[ColumnType.Date]: 'date'.toUpperCase(),
		[ColumnType.BigInt]: 'biginteger'.toUpperCase(),
		[ColumnType.Bin]: 'binary'.toUpperCase(),
		[ColumnType.Words]: 'words'.toUpperCase(),
		[ColumnType.Int]: 'integer'.toUpperCase(),
		[ColumnType.Bool]: 'boolean'.toUpperCase(),
		[ColumnType.Geo]: 'geometry'.toUpperCase(),
	};

	private readonly ColumnTypeMap: { [k in SupportedTypes]: ColumnType } = {
		[SupportedTypes.SIMPLE_TYPES_STRING_ITEM]: ColumnType.Str,
		[SupportedTypes.SIMPLE_TYPES_INTEGER_ITEM]: ColumnType.Int,
		[SupportedTypes.SIMPLE_TYPES_BIG_INTEGER_ITEM]: ColumnType.BigInt,
		[SupportedTypes.SIMPLE_TYPES_DECIMAL_ITEM]: ColumnType.Dec,
		[SupportedTypes.SIMPLE_TYPES_DATE_ITEM]: ColumnType.Date,
		[SupportedTypes.SIMPLE_TYPES_BOOLEAN_ITEM]: ColumnType.Bool,
		[SupportedTypes.TYPES_WORDS_ITEM]: ColumnType.Words,
		[SupportedTypes.TYPES_FILE_ITEM]: ColumnType.File,
		[SupportedTypes.TYPES_BINARY_ITEM]: ColumnType.Bin,
		[SupportedTypes.TYPES_GEOMETRY_ITEM]: ColumnType.Geo,
	};

	private readonly DepotTypeMap: { [k: string]: DepotTypes } = {
		SQL: DepotTypes.SQL,
		DB: DepotTypes.SQL,
		MONGODB: DepotTypes.MONGODB,
		CSV: DepotTypes.CSV,
		S3CSV: DepotTypes.CSV,
		SFTPCSV: DepotTypes.CSV,
		HDFS_CSV: DepotTypes.CSV,
		HTTP_CSV: DepotTypes.CSV,
		PARQUET: DepotTypes.PARQUET,
		S3_PARQUET: DepotTypes.PARQUET,
		SFTP_PARQUET: DepotTypes.PARQUET,
		HDFS_PARQUET: DepotTypes.PARQUET,
		HTTP_PARQUET: DepotTypes.PARQUET,
		JSON: DepotTypes.JSON,
		SFTP_JSON: DepotTypes.JSON,
		S3_JSON: DepotTypes.JSON,
		HDFS_JSON: DepotTypes.JSON,
		HTTP_JSON: DepotTypes.JSON,
		JSON_V2: DepotTypes.JSON_V2,
		EXCEL: DepotTypes.EXCEL,
		SFTP_EXCEL: DepotTypes.EXCEL,
		S3_EXCEL: DepotTypes.EXCEL,
		HDFS_EXCEL: DepotTypes.EXCEL,
		HTTP_EXCEL: DepotTypes.EXCEL,
		S3_BINARY: DepotTypes.BINARY,
		SFTP_BINARY: DepotTypes.BINARY,
		HDFS_BINARY: DepotTypes.BINARY,
		BINARY: DepotTypes.BINARY,
		XML_V2: DepotTypes.XML_V2,
		TRINO: DepotTypes.TRINO,
		XML: DepotTypes.XML,
		S3_XML: DepotTypes.XML,
		SFTP_XML: DepotTypes.XML,
		HDFS_XML: DepotTypes.XML,
		HTTP_XML: DepotTypes.XML,
		NEO4J: DepotTypes.NEO4J,
	};

	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly dxListsStorage: DxListsStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly userSettings: ProjectStorage
	) {}

	public generateDepotList(
		response: ReadonlyArray<IDepotResponse>
	): DepotListEntity {
		const list = this.getDepotColumns();
		const tuples = response
			.reduce<Array<DepotItemEntity>>((acc, curr) => {
				const entity = this.generateDepotItemEntity(curr);
				acc.push(entity);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, DepotItemEntity>(
			tuples.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	public generateDepotHeaderList(
		response: ReadonlyArray<IDepotHeaderResponse>
	): DepotHeaderListEntity {
		const list = DepotHeaderListEntity.build([
			{
				field: 'position',
				alias: $localize`:@@depot.modal.entity.columns.column.position:`,
				type: 'string',
				isSearchable: false,
				visible: false,
				visibleIndex: 0,
			},
			{
				field: 'isSelected',
				alias: $localize`:@@depot.modal.entity.columns.column.selectable:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 1,
				width: '5%',
			},
			{
				field: 'headerType',
				alias: $localize`:@@depot.modal.entity.columns.column.header.type:`,
				type: 'string',
				isSearchable: false,
				visibleIndex: 2,
				width: '5%',
			},
			{
				field: 'isList',
				alias: $localize`:@@depot.modal.entity.columns.column.isList:`,
				type: 'boolean',
				isSearchable: false,
				visibleIndex: 3,
				width: '5%',
			},
			{
				field: 'header',
				alias: $localize`:@@depot.modal.entity.columns.column.header:`,
				type: 'string',
				isSearchable: false,
				visibleIndex: 4,
				width: '15%',
			},
			{
				field: 'label',
				alias: $localize`:@@depot.modal.entity.columns.column.label:`,
				type: 'string',
				isSearchable: false,
				visibleIndex: 5,
				width: '15%',
			},
			{
				field: 'columnType',
				alias: $localize`:@@depot.modal.entity.columns.column.type:`,
				type: 'string',
				isSearchable: false,
				visibleIndex: 6,
				width: '15%',
			},
			{
				field: 'format',
				alias: $localize`:@@depot.modal.entity.columns.column.format:`,
				type: 'string',
				isSearchable: false,
				visibleIndex: 7,
				width: '40%',
			},
		]);

		const tuples = response
			.reduce<Array<DepotHeaderItemEntity>>((acc, curr) => {
				const entity = this.generateDepotHeaderEntity(curr);
				acc.push(entity);
				return acc;
			}, [])
			.sort((a, b) => a.position - b.position);

		const orderedMap = OrderedMap<string, DepotHeaderItemEntity>(
			tuples.map((item) => [`${item.id}`, item])
		);

		return list.setElements(orderedMap);
	}

	public generateDepotSchemaHeaderMapping(
		payload: IDepotSchemaModel
	): Array<DepotSchemaEntity> {
		return [
			DepotSchemaEntity.buildRoot({
				children:
					payload?.schema?.children.map((c) =>
						this.parseSchemaDefinition(c, this.ROOT_PARENT_LABEL, 0)
					) ?? [],
			}),
		];
	}

	public generateDepotSchemaHeaderPayload(
		rootChildren: Array<DepotSchemaEntity>
	): IDepotSchemaModel {
		return {
			schema: {
				type: 'ROOT',
				children: rootChildren.map((m) => this.generatePayload(m)),
				keep: false,
			},
			headers: new Array<string>(),
		};
	}

	public updateListWithEntityCount(
		list: DepotListEntity,
		result: ReadonlyArray<IDepotEntityBulkCount>
	): DepotListEntity {
		return list.updateElements(result.map((it) => [it.id, it.count]));
	}

	public generateSupportedLanguages(
		languages: ReadonlyArray<ISupportedLanguage>
	): Map<string, SupportedLanguageEntity> {
		const tuples = languages.reduce((acc, curr) => {
			const entity = SupportedLanguageEntity.build({
				id: randUuid(),
				key: curr.value,
				label: curr.label,
			});
			acc.push(entity);
			return acc;
		}, new Array<SupportedLanguageEntity>());

		return OrderedMap<string, SupportedLanguageEntity>(
			tuples.map((item) => [`${item.id}`, item])
		);
	}

	public generateAvailableTypes(
		types: ReadonlyArray<ICharacteristicType>
	): Map<number, CharacteristicType> {
		const items = types.reduce<Array<CharacteristicType>>((acc, curr) => {
			acc.push({
				id: curr.id,
				value: this.ColumnTypeMap[curr.code],
				label: curr.lib,
			});
			return acc;
		}, []);

		return Map<number, CharacteristicType>(
			items.map((item) => [item.id, item])
		);
	}

	public generateAvailableDateFormats(
		dateFormats: ReadonlyArray<IDateFormat>
	): Map<string, DcTimestampFormat> {
		const items = dateFormats.reduce<Array<DcTimestampFormat>>((acc, curr) => {
			acc.push({
				label: curr.label,
				value: curr.value,
			});
			return acc;
		}, []);

		return toImmutableMap(items, 'value');
	}

	private getDepotColumns(): DepotListEntity {
		let availableCols: Array<IDepotListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@depot.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@depot.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'projectLabel',
				alias: $localize`:i18n=@@depot.list.column.project:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 3,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@depot.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@depot.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 5,
			},

			{
				field: 'connectorLabel',
				alias: $localize`:i18n=@@depot.list.column.connector:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 6,
			},
			{
				field: 'typeDescription',
				alias: $localize`:i18n=@@depot.list.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 7,
			},
			{
				field: 'isImported',
				alias: $localize`:i18n=@@depot.list.column.imported:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 8,
				visible: false,
			},
			{
				field: 'entitiesCount',
				alias: $localize`:i18n=@@depot.list.column.entitiesCount:`,
				type: 'number',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 9,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@depot.list.column.creationDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 10,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@depot.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 11,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@depot.list.column.updateDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 12,
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@depot.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 13,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			IDepotListExtra,
			DepotItemEntity
		>(DcDxGrids.DepotListStandalone, availableCols);
		return DepotListEntity.build(availableCols);
	}

	private generateDepotItemEntity(response: IDepotResponse): DepotItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		const elemProjectId = response.project?.id ?? -1;
		const belongsToProject = this.userSettings.getProjectId() === elemProjectId;
		return DepotItemEntity.build({
			id: response.id,
			isInSameProject: belongsToProject,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			projectLabel: response.project?.label ?? '',
			projectPictoUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.project?.icon_id ?? -1
			}`,
			isProjectPublic: response.project?.is_public ?? false,
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
			isImported: response.imported,
			connectorUrl: response.connector?.id
				? this.parseConnectorPageUrl(
						response.connector.id,
						response.connector.type ?? ''
				  )
				: '',
			connectorLabel: this.getConnectorLabel(
				response.connector?.label ?? '',
				response.connector?.type ?? ''
			),
			connectorType: response.connector?.type ?? '',
			type: this.DepotTypeMap[response.type.toUpperCase()] ?? '',
			typeDescription: this.getDepotTypeDescription(response.type),
			entitiesCount: response.entitiesCount,
		});
	}

	private parseConnectorPageUrl(
		connectorId: number,
		connectorType: string
	): string {
		const projectId = this.userSettings.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			connectorId,
			'CONNECTOR',
			connectorType as ConnectorSubType
		);
	}

	private getConnectorLabel(
		connectorLabel: string,
		connectorType: string
	): string {
		let type = '';
		switch (connectorType) {
			case 'SQL':
				type = '(Bdd)';
				break;
			case 'HTTP':
				type = '(HTTP)';
				break;
			case 'S3':
				type = '(S3 / MinIO)';
				break;
			case 'SFTP':
				type = '(SFTP)';
				break;
			case 'HDFS':
				type = '(HDFS)';
				break;
			case 'NEO4J':
				type = '(Bdd)';
				break;
			case 'MONGODB':
				type = '(Bdd)';
				break;
			case 'TRINO':
				type = '(Bdd)';
				break;
			default:
				type = '()';
				break;
		}

		const label = connectorType
			? connectorLabel
			: $localize`:i18n=@@depot.list.connector.localMode:`;
		return `${label} ${type}`;
	}

	private getDepotTypeDescription(type: string): string {
		if (type.includes('CSV')) {
			return $localize`:i18n=@@depot.list.type.description.csv:`;
		}
		if (type.includes('EXCEL')) {
			return $localize`:i18n=@@depot.list.type.description.excel:`;
		}
		if (type.includes('XML')) {
			return type.includes('V2')
				? $localize`:i18n=@@depot.list.type.description.xml-v2:`
				: $localize`:i18n=@@depot.list.type.description.xml:`;
		}
		if (type.includes('JSON')) {
			return type.includes('V2')
				? $localize`:i18n=@@depot.list.type.description.json-v2:`
				: $localize`:i18n=@@depot.list.type.description.json:`;
		}
		if (type.includes('PARQUET')) {
			return $localize`:i18n=@@depot.list.type.description.parquet:`;
		}
		if (type.includes('BINARY')) {
			return $localize`:i18n=@@depot.list.type.description.binary:`;
		}
		switch (type) {
			case 'SQL':
				return $localize`:i18n=@@depot.list.type.description.sql:`;
			case 'NEO4J':
				return $localize`:i18n=@@depot.list.type.description.neo4j:`;
			case 'MONGODB':
				return $localize`:i18n=@@depot.list.type.description.mongodb:`;
			case 'TRINO':
				return $localize`:i18n=@@depot.list.type.description.trino:`;
		}
		return '';
	}

	private generateDepotHeaderEntity(
		response: IDepotHeaderResponse
	): DepotHeaderItemEntity {
		return DepotHeaderItemEntity.build({
			id: response.id,
			label: response.column ?? '',
			columnType: this.getColumnType(response.data_type),
			isList: response.list,
			format: response.date_pattern,
			header: response.column ?? '',
			headerType: this.getColumnType(response.data_type),
			position: response.position ?? -1,
			isSelected: !response.depot_details,
			depotDetails: response.depot_details ?? false,
		});
	}

	private getColumnType(type: ColumnTypeModel): ColumnType {
		switch (type) {
			case ColumnTypeModel.STRING_TYPE:
				return ColumnType.Str;
			case ColumnTypeModel.FILE_TYPE:
				return ColumnType.File;
			case ColumnTypeModel.DECIMAL_TYPE:
				return ColumnType.Dec;
			case ColumnTypeModel.DATE_TYPE:
				return ColumnType.Date;
			case ColumnTypeModel.BIG_INTEGER_TYPE:
				return ColumnType.BigInt;
			case ColumnTypeModel.BINARY_TYPE:
				return ColumnType.Bin;
			case ColumnTypeModel.WORDS_TYPE:
				return ColumnType.Words;
			case ColumnTypeModel.INTEGER_TYPE:
				return ColumnType.Int;
			case ColumnTypeModel.BOOLEAN_TYPE:
				return ColumnType.Bool;
			case ColumnTypeModel.GEOMETRY_TYPE:
				return ColumnType.Geo;
			default:
				return ColumnType.Unknown;
		}
	}

	private parseSchemaDefinition(
		model: ISchemaDefinitionModel,
		parentLabel: string,
		depth: number
	): DepotSchemaEntity {
		if (model.type === 'LEAF') {
			return DepotSchemaEntity.buildLeaf({
				id: randUuid(),
				depth,
				originalLabel: model.element_name,
				currentLabel: model.final_name,
				isSerialized: model.convert_to === 'STRING',
				isSelected: model.keep,
				shouldIgnoreSubtree: model.ignore_subtree,
				dataType: model.data_type
					? this.dataTypeMapper[model.data_type.toLowerCase()]
					: ColumnType.Unknown,
				position: model.position,
				convertTo: model.convert_to
					? this.dataTypeMapper[model.convert_to.toLowerCase()]
					: null,
				format: model.convert_to_format ? model.convert_to_format : null,
				timezone: model.convert_to_tz ? model.convert_to_tz : null,
				language: model.convert_to_language ? model.convert_to_language : null,
			}).setPath(parentLabel, model.element_name ?? '');
		}

		if (model.type === 'STRUCT') {
			const isSerialized = model.keep;
			const structEntry = DepotSchemaEntity.buildStruct({
				id: randUuid(),
				depth,
				originalLabel: model.element_name,
				currentLabel: model.final_name,
				isSerialized,
				isSelected: isSerialized,
				shouldIgnoreSubtree: model.ignore_subtree,
				position: model.position,
			})
				.updateSelection(isSerialized)
				.setPath(parentLabel, model.element_name ?? '');

			return structEntry.updateChildren(
				model.children.map((c) =>
					this.parseSchemaDefinition(c, structEntry.path, depth + 1)
				)
			);
		}

		if (model.type === 'ARRAY') {
			let isSerialized = false;
			if (model.keep && model.children[0].type !== 'LEAF') {
				isSerialized = true;
			} else if (model.keep && model.children[0].type === 'LEAF') {
				isSerialized = model.convert_to === 'STRING';
			}
			const arrayOfValues = DepotSchemaEntity.buildArray({
				id: randUuid(),
				originalLabel: model.element_name ?? this.ELEMENT_LABEL,
				currentLabel: model.final_name ?? this.ELEMENT_LABEL,
				convertTo:
					!isSerialized && model.convert_to
						? this.dataTypeMapper[model.convert_to]
						: null,
				shouldIgnoreSubtree: model.ignore_subtree,
				depth,
				position: model.position,
				isSerialized,
			}).setPath(parentLabel, model.element_name ?? '');

			return arrayOfValues
				.updateChildren(
					model.children.map((c) =>
						this.parseSchemaDefinition(c, arrayOfValues.path, depth + 1)
					)
				)
				.updateSelection(model.keep);
		}

		return DepotSchemaEntity.buildLeaf({});
	}

	private generatePayload(mapping: IDepotSchema): ISchemaDefinitionModel {
		if (mapping.schemaElemType === SchemaDataStructure.Leaf) {
			const depotMapping: ISchemaDefinitionModel = {
				['element_name']: mapping.originalLabel,
				['final_name']: mapping.currentLabel,
				['data_type']: mapping.dataType
					? this.typeMapper[mapping.dataType]
					: '',
				children: [],
				['convert_to']: mapping.isSerialized
					? 'STRING'
					: mapping.convertTo
					? this.typeMapper[mapping.convertTo]
					: null,
				keep: mapping.isSelected,
				type: 'LEAF',
				['ignore_subtree']: mapping.shouldIgnoreSubtree,
			};

			let updateDepotMapping = depotMapping;
			if (mapping.format && !mapping.isSerialized) {
				updateDepotMapping = {
					...updateDepotMapping,
					['convert_to_format']: mapping.format,
				};
			}
			if (mapping.timezone && !mapping.isSerialized) {
				updateDepotMapping = {
					...updateDepotMapping,
					['convert_to_tz']: mapping.timezone,
				};
			}

			if (mapping.language && !mapping.isSerialized) {
				updateDepotMapping = {
					...updateDepotMapping,
					['convert_to_language']: mapping.language,
				};
			}

			if (mapping.position !== -1) {
				updateDepotMapping = {
					...updateDepotMapping,
					position: mapping.position,
				};
			}
			return updateDepotMapping;
		}

		if (mapping.schemaElemType === 'STRUCT') {
			const depotMapping: ISchemaDefinitionModel = {
				['element_name']: mapping.originalLabel,
				['final_name']: mapping.currentLabel,
				children: mapping.children.map((m) => this.generatePayload(m)),
				['convert_to']: mapping.isSelected ? 'STRING' : null,
				keep: mapping.isSelected,
				type: 'STRUCT',
				['ignore_subtree']: mapping.shouldIgnoreSubtree,
			};
			let updateDepotMapping = depotMapping;
			if (mapping.position !== -1) {
				updateDepotMapping = {
					...updateDepotMapping,
					position: mapping.position,
				};
			}
			return updateDepotMapping;
		}

		// [ ]
		if (mapping.schemaElemType === SchemaDataStructure.Array) {
			let shouldBeSerialized;
			if (mapping.children[0].schemaElemType !== SchemaDataStructure.Leaf) {
				shouldBeSerialized = mapping.isSelected;
			} else {
				shouldBeSerialized = mapping.isSelected && mapping.isSerialized;
			}
			const depotMapping: ISchemaDefinitionModel = {
				['element_name']: mapping.originalLabel,
				['final_name']: mapping.currentLabel,
				children: mapping.children.map((m) => this.generatePayload(m)),
				['convert_to']: shouldBeSerialized ? 'STRING' : null,
				['data_type']: undefined,
				keep: mapping.isSelected,
				type: 'ARRAY',
				['ignore_subtree']: mapping.shouldIgnoreSubtree,
			};
			let updateDepotMapping = depotMapping;
			if (mapping.position !== -1) {
				updateDepotMapping = {
					...updateDepotMapping,
					position: mapping.position,
				};
			}
			return updateDepotMapping;
		}

		return {} as ISchemaDefinitionModel;
	}
}
